import type { ConciergeCard, ConciergePrefs, ConciergeResponse, TailMsg, PendingContact } from "@/hooks/useAiConcierge";
import type { Tenant } from "@/lib/tenants";
import type { WelcomePrefs } from "@/lib/welcomePrefs";

// The renter concierge transcript as both surfaces (home widget, inbox
// thread) render it. Pure helpers only, no React, so they can be unit
// tested and shared by the conversation hook and the inbox list row.
//
// The server transcript (`display` on ai_chat_sessions) is the source of
// truth for the ordered messages; items derived from it carry ids `s<index>`.
// Everything the server never stores, the greeting, system notes, failed
// sends, is a local extra with an `anchor`: the number of server entries
// that existed when it was added, i.e. it renders just before `s<anchor>`.

export type TranscriptRole = "user" | "assistant" | "agent" | "system";

export type TranscriptKind =
  | "greeting"
  | "handoff"
  | "failed" // a send that never reached the server; can be retried
  | "error" // a send the server refused for good (concierge disabled)
  | "session_full"
  | "request_sent"
  | "confirm_contact"; // a proposed request waiting for the renter's tap

export interface TranscriptItem {
  /** Stable React key. `s<i>` = the i-th entry of the server transcript, `l<n>` = a local extra. */
  id: string;
  role: TranscriptRole;
  text: string;
  cards?: ConciergeCard[];
  /** ISO timestamp; server entries carry the server's, optimistic ones the client's. */
  at?: string;
  /** Never on the server: the greeting, system notes, failed sends. */
  local?: true;
  /** Local extras only: how many server entries existed when it was added. */
  anchor?: number;
  kind?: TranscriptKind;
  /** Caption under a failed send (why it did not go through). */
  note?: string;
  /** kind "confirm_contact": the request the renter can send with one tap. */
  pending?: PendingContact;
}

let localCounter = 0;
/** Fresh id for a local-only item. */
export const localId = (): string => `l${++localCounter}`;

/** The Send card for a request the concierge proposed. */
export const confirmItem = (pending: PendingContact, anchor: number): TranscriptItem => ({
  id: localId(),
  role: "system",
  text: `Send this request to the owner of "${pending.title}"?`,
  local: true,
  anchor,
  kind: "confirm_contact",
  pending,
});

export const SYSTEM_COPY = {
  handoff: "A team member has joined and will reply here.",
  handoff_requested: "A person from the team will reply here soon.",
  request_sent: "Your request was sent. The owner will reply in Messages.",
  session_full: "This chat is full. Start a new one and I'll pick up with fresh searches.",
} as const;

export const ERROR_COPY = {
  disabled: "The concierge isn't available here right now.",
  network: "Not sent. I couldn't get through just then.",
  offline: "The concierge is offline right now, please try again soon.",
} as const;

export const greetingItem = (text: string): TranscriptItem => ({
  id: "greeting",
  role: "assistant",
  text,
  local: true,
  anchor: 0,
  kind: "greeting",
});

export const noteItem = (kind: TranscriptKind, text: string, anchor: number): TranscriptItem => ({
  id: localId(),
  role: "system",
  text,
  local: true,
  anchor,
  kind,
});

const serverIndex = (m: TranscriptItem): number => (m.local ? -1 : Number(m.id.slice(1)));

/** How many server-shaped entries the transcript holds (in-flight sends included). */
export const serverCount = (items: ReadonlyArray<TranscriptItem>): number =>
  items.reduce((n, m) => (m.local ? n : Math.max(n, serverIndex(m) + 1)), 0);

const asRole = (r: string | undefined): TranscriptRole =>
  r === "user" || r === "agent" || r === "assistant" ? r : "assistant";

export const serverItem = (m: TailMsg, index: number): TranscriptItem => ({
  id: `s${index}`,
  role: asRole(m.role),
  text: typeof m.text === "string" ? m.text : "",
  ...(m.cards && m.cards.length > 0 ? { cards: m.cards } : {}),
  ...(m.at ? { at: m.at } : {}),
});

const sameItem = (a: TranscriptItem, b: TranscriptItem): boolean =>
  a.id === b.id &&
  a.text === b.text &&
  a.at === b.at &&
  a.role === b.role &&
  (a.cards?.length ?? 0) === (b.cards?.length ?? 0);

/**
 * Reconcile the local transcript with the server's. Server entries win in
 * their order; local server-shaped items past the end (a send still in
 * flight) stay after them; local extras keep their anchored spot. Idempotent,
 * applying the same tail twice returns the same array instance, so a poll
 * never reorders or wipes anything.
 */
export const mergeTranscript = (
  current: ReadonlyArray<TranscriptItem>,
  display: ReadonlyArray<TailMsg>,
): TranscriptItem[] => {
  const server = display.map(serverItem);
  const n = server.length;
  const inflight = current.filter((m) => !m.local && serverIndex(m) >= n);
  const extras = current.filter((m) => m.local);
  const ordered = [...server, ...inflight.map((m, j) => ({ ...m, id: `s${n + j}` }))];
  const out: TranscriptItem[] = [];
  for (let i = 0; i < ordered.length; i++) {
    for (const e of extras) if ((e.anchor ?? 0) === i) out.push(e);
    out.push(ordered[i]);
  }
  for (const e of extras) if ((e.anchor ?? 0) >= ordered.length) out.push(e);
  const unchanged = out.length === current.length && out.every((m, i) => sameItem(m, current[i]));
  return unchanged ? (current as TranscriptItem[]) : out;
};

export type Gate = "need_auth" | "need_profile" | null;

export interface PresentedResponse {
  items: TranscriptItem[];
  gate: Gate;
  handoff: boolean;
  sessionFull: boolean;
  prefs: ConciergePrefs | null;
}

/**
 * Turn the edge function's answer into the next transcript. `sent` is the
 * optimistic user item (`s<N>`) already in `items`. What the server persisted
 * decides the shape: a normal turn stores the user + assistant lines, a
 * handoff turn only the user line, and the cap / offline / error paths store
 * nothing, so those must not leave a server-shaped item behind.
 */
export const presentResponse = (
  res: ConciergeResponse,
  items: ReadonlyArray<TranscriptItem>,
  sent: TranscriptItem,
  nowIso: string,
): PresentedResponse => {
  const n = serverIndex(sent);
  const without = items.filter((m) => m.id !== sent.id);
  const failed = (kind: "failed" | "error", note: string): TranscriptItem => ({
    ...sent,
    id: localId(),
    local: true,
    anchor: n,
    kind,
    note,
  });
  const base = { gate: null as Gate, handoff: false, sessionFull: false, prefs: res.prefs ?? null };

  if (res.error) {
    const disabled = res.error === "concierge_disabled";
    return {
      ...base,
      items: [...without, failed(disabled ? "error" : "failed", disabled ? ERROR_COPY.disabled : ERROR_COPY.network)],
    };
  }
  if (res.handoff) return { ...base, handoff: true, items: [...items] };
  if (res.action === "handoff_requested") {
    const reply: TranscriptItem = { id: `s${n + 1}`, role: "assistant", text: res.reply ?? "", at: nowIso };
    return { ...base, handoff: true, items: [...items, reply, noteItem("handoff", SYSTEM_COPY.handoff_requested, n + 2)] };
  }
  if (res.action === "session_full") {
    return {
      ...base,
      sessionFull: true,
      items: [...without, noteItem("session_full", res.reply || SYSTEM_COPY.session_full, n)],
    };
  }
  if (res.action === "offline" || res.action === "error") {
    return { ...base, items: [...without, failed("failed", res.reply || ERROR_COPY.offline)] };
  }
  const reply: TranscriptItem = {
    id: `s${n + 1}`,
    role: "assistant",
    text: res.reply ?? "",
    ...(res.cards && res.cards.length > 0 ? { cards: res.cards } : {}),
    at: nowIso,
  };
  const next = [...items, reply];
  if (res.action === "request_sent") next.push(noteItem("request_sent", SYSTEM_COPY.request_sent, n + 2));
  if (res.pendingContact) next.push(confirmItem(res.pendingContact, n + 2));
  const gate: Gate = res.action === "need_auth" ? "need_auth" : res.action === "need_profile" ? "need_profile" : null;
  return { ...base, gate, items: next };
};

/** Every listing surfaced across the conversation, deduped by slug, in first-seen order. */
export const shownListingsOf = (items: ReadonlyArray<Pick<TranscriptItem, "cards">>): ConciergeCard[] => {
  const out: ConciergeCard[] = [];
  const seen = new Set<string>();
  for (const m of items) {
    for (const c of m.cards ?? []) {
      if (!seen.has(c.slug)) {
        seen.add(c.slug);
        out.push(c);
      }
    }
  }
  return out;
};

/** The newest server entry, for the inbox row's preview line. */
export const lastTranscriptLine = (display: ReadonlyArray<TailMsg> | null | undefined): TailMsg | null =>
  display && display.length > 0 ? display[display.length - 1] : null;

export const hasSavablePrefs = (p: ConciergePrefs | null): boolean =>
  !!p &&
  (p.budgetMin != null ||
    p.budgetMax != null ||
    !!p.preferredAreas?.length ||
    p.preferredBedrooms != null ||
    !!p.preferredTypes?.length ||
    !!p.preferredAmenities?.length);

/** Map the chat's collected prefs into the welcome-wizard shape the existing
 *  alert/registration funnel consumes. Areas come back as display names; the
 *  funnel expects slugs, so resolve them against the tenant's area taxonomy. */
export const conciergePrefsToWelcome = (p: ConciergePrefs, tenant: Tenant): WelcomePrefs => {
  const nameToSlug = new Map<string, string>();
  tenant.areas.forEach((name, i) => {
    const slug = tenant.areaSlugs[i];
    if (slug) nameToSlug.set(name.toLowerCase(), slug);
  });
  const toSlug = (n: string) =>
    nameToSlug.get(n.toLowerCase()) ?? n.toLowerCase().trim().replace(/\s+/g, "-");
  const month = p.moveInMonths?.[0];
  return {
    email: "",
    preferred_areas: (p.preferredAreas ?? []).map(toSlug).filter(Boolean),
    preferred_bedrooms: p.preferredBedrooms ?? undefined,
    preferred_budget_min: p.budgetMin ?? undefined,
    preferred_budget_max: p.budgetMax ?? undefined,
    preferred_types: p.preferredTypes ?? [],
    preferred_amenities: p.preferredAmenities ?? [],
    move_in_date: month ? `${month}-01` : undefined,
  };
};
